import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Card,
    Typography,
    Button,
    Autocomplete,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Grid,
    useMediaQuery,
    useTheme,
    MenuItem,
    Alert
} from '@mui/material';
import { CloudUpload, Image as ImageIcon, AddPhotoAlternate, Folder } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import PageHeaderLayout from '../../../../components/Layout/PageHeaderLayout';
import ImgUploadDialog from '../../../../components/ImgUpload/imgUpload';
import MultiFileUploadDialog from '../../../../components/ImgUpload/MultipleImageUpload';
import UploadedImageList from './UploadedImageList';

const toDisplayName = (folder) => {
    if (!folder) return '';
    return folder.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const UploadImagePage = () => {
    const menuDispatch = useDispatch();
    menuDispatch({
        type: 'menuSelect',
        payload: 'media-upload'
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [folderList, setFolderList] = useState([
            'profile',
            'service',
            'category',
            'subcategory',
            'banner/home/slider',
            'storefront-logo',
            'storefront-banner',
            'storefront-gallery',
            'kyc',
            'expert-gallery',
            'brands',
            'booking-payment',
            'expert-wallet',
            'whatsapp/campaign',
            'customer-app/sections',
            'customer-app/items',
            'sdui'
        ]);
    const [uploadedImages, setUploadedImages] = useState([]);

    const [imgModalOpen, setImgModalOpen] = useState(false);
    const [multiImgModalOpen, setMultiImgModalOpen] = useState(false);

    const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            folder: null,
            uploadType: 'single'
        }
    });

    const uploadType = watch('uploadType');
    const selectedFolder = watch('folder');

    const folderOptions = folderList.map(folder => ({
        value: folder,
        label: toDisplayName(folder)
    }));

    const handleSingleImageUpload = (uploadedImageData) => {
        if (uploadedImageData) {
    //         {
    //     "imageBaseUrl": "https://cudel.s3.amazonaws.com/",
    //     "imageDirectory": "staging/profile/",
    //     "imageSubDirectory": "sm, md, lg",
    //     "imageName": "beelabstech_logo-1773665942173.jpg"
    // }
            const imageSubDirectory = uploadedImageData.imageSubDirectory ? uploadedImageData.imageSubDirectory.split(',').map(dir => dir.trim()) : [];
            const imageUrl = `${uploadedImageData.imageBaseUrl}${uploadedImageData.imageDirectory}${imageSubDirectory.includes('md') ? 'md/' : ''}${uploadedImageData.imageName}`;
            // setUploadedImages(prev => [...prev, {
            // const imageUrl = `${uploadedImageData.imageBaseUrl}${uploadedImageData.imageDirectory}md/${uploadedImageData.imageName}`;
            setUploadedImages(prev => [...prev, {
                name: uploadedImageData.imageName,
                url: imageUrl,
                data: uploadedImageData
            }]);
            Swal.fire({
                icon: 'success',
                title: 'Upload Successful',
                text: 'Image uploaded successfully',
                timer: 1500,
            });
        }
    };

    const handleMultipleImageUpload = (uploadedImagesData) => {
        if (uploadedImagesData && uploadedImagesData.length > 0) {
            const newImages = uploadedImagesData.map(img => ({
                name: img.imageName,
                // url: `${img.imageBaseUrl}${img.imageDirectory}md/${img.imageName}`,
                url: `${img.imageBaseUrl}${img.imageDirectory}${img.imageSubDirectory && img.imageSubDirectory.includes('md') ? 'md/' : ''}${img.imageName}`,
                data: img
            }));
            setUploadedImages(prev => [...prev, ...newImages]);
            Swal.fire({
                icon: 'success',
                title: 'Upload Successful',
                text: `${uploadedImagesData.length} image(s) uploaded successfully`,
                timer: 2000,
            });
        }
    };

    const removeImage = (index) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    const clearAll = () => {
        setUploadedImages([]);
        reset();
    };

    const [imgData, setImgData] = useState({
        path: selectedFolder ,
        img_type: 'single'
    });

    useEffect(() => {
        if(selectedFolder){
            setImgData({
                path: selectedFolder,
                img_type: 'single'
            });
            setUploadedImages([]);
        }
    }, [selectedFolder, uploadType]);


    return (
        <>
            <PageHeaderLayout headerTitle={'Upload Images'} />
            
            <Grid container spacing={1}>
                <Grid item xs={12} md={5}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: '1.35%', borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                            <Typography  sx={{ fontWeight: 600, fontSize: '.85rem', color: '#333' }}>
                                Upload Settings
                            </Typography>
                        </Box>
                        
                        <Box component="form" onSubmit={handleSubmit(() => {})} sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Controller
                                name="folder"
                                control={control}
                                rules={{ required: 'Please select a folder' }}
                                render={({ field }) => (
                                    <Autocomplete
                                        {...field}
                                        options={folderOptions}
                                        value={folderOptions.find(opt => opt.value === field.value) || null}
                                        onChange={(event, newValue) => field.onChange(newValue?.value ?? null)}
                                        getOptionLabel={(option) => option.label || ''}
                                        isOptionEqualToValue={(option, value) => option.value === value?.value}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Folder"
                                                error={!!errors.folder}
                                                helperText={errors.folder?.message}
                                                size="small"
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <MenuItem {...props} key={option.value} sx={{fontSize:'0.75rem'}}>
                                                {option.label}
                                            </MenuItem>
                                        )}
                                    />
                                )}
                            />

                            <FormControl component="fieldset" error={!!errors.uploadType}>
                                <FormLabel component="legend" sx={{ fontSize: '0.875rem', color: '#333', mb: 1 }}>
                                    Upload Type
                                </FormLabel>
                                <Controller
                                    name="uploadType"
                                    control={control}
                                    render={({ field }) => (
                                        <RadioGroup {...field} row={!isMobile} column={isMobile}>
                                            <FormControlLabel
                                                value="single"
                                                control={<Radio size="small" />}
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <ImageIcon sx={{ fontSize: 18 }} />
                                                        <Typography variant="body2">Single Image</Typography>
                                                    </Box>
                                                }
                                                sx={{ mr: 3 }}
                                            />
                                            <FormControlLabel
                                                value="multiple"
                                                control={<Radio size="small" />}
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AddPhotoAlternate sx={{ fontSize: 18 }} />
                                                        <Typography variant="body2">Multiple Images</Typography>
                                                    </Box>
                                                }
                                            />
                                        </RadioGroup>
                                    )}
                                />
                            </FormControl>

                            {!selectedFolder && (
                                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                                    Please select a folder first to enable upload options
                                </Alert>
                            )}

                            {selectedFolder && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                                    {uploadType === 'single' && 
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        size="large"
                                        startIcon={<ImageIcon />}
                                        onClick={()=> setImgModalOpen(true)}
                                        sx={{
                                            py: 1.5,
                                            borderColor: '#3a2bde',
                                            color: '#3a2bde',
                                            '&:hover': {
                                                borderColor: '#3a2bde',
                                                bgcolor: 'rgba(58, 43, 222, 0.04)',
                                            }
                                        }}
                                    >
                                        Upload Single Image
                                    </Button>
                                    }
                                    {uploadType === 'multiple' && (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            startIcon={<CloudUpload />}
                                            onClick={()=>setMultiImgModalOpen(true)}
                                            sx={{
                                                py: 1.5,
                                                fontWeight: 600,
                                                bgcolor: '#3a2bde',
                                                boxShadow: '0 4px 14px rgba(58, 43, 222, 0.3)',
                                                '&:hover': {
                                                    bgcolor: '#2d1fba',
                                                    boxShadow: '0 6px 20px rgba(58, 43, 222, 0.4)',
                                                }
                                            }}
                                        >
                                            Upload Multiple Images
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {uploadedImages.length > 0 && (
                                <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="body2" color="primary" fontWeight={500}>
                                            {uploadedImages.length} image{uploadedImages.length > 1 ? 's' : ''} uploaded
                                        </Typography>
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={clearAll}
                                        >
                                            Clear All
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 400 }}>
                        <Box sx={{ p:'1%', borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#333' }}>
                                {uploadedImages.length > 0 ? 'Uploaded Images' : 'No Images Yet'}
                            </Typography>
                            {uploadedImages.length > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''}
                                </Typography>
                            )}
                        </Box>
                        
                        <Box sx={{ flex: 1, p: 3, overflow: 'auto', bgcolor: '#fafafa' }}>
                            {uploadedImages.length === 0 ? (
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    height: '100%',
                                    py: 8 
                                }}>
                                    <Folder sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                                    <Typography variant="h6" sx={{ color: '#888', mb: 1, fontWeight: 500 }}>
                                        No images uploaded yet
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#aaa', textAlign: 'center' }}>
                                        Select a folder and click upload buttons to add images
                                    </Typography>
                                </Box>
                            ) : (
                                <Grid container spacing={2}>
                                    <UploadedImageList uploadedFiles={uploadedImages} />
                                </Grid>
                            )}
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            <ImgUploadDialog
                // key={selectedFolder}
                open={imgData.path && imgModalOpen}
                onClose={() => setImgModalOpen(false)}
                imgData={{
                    path: selectedFolder,
                    img_type: 'single'
                }}
                imgUploadResponse={handleSingleImageUpload}
                trigStatus={false}
            />

            <MultiFileUploadDialog
                open={imgData.path && multiImgModalOpen}
                onClose={() => setMultiImgModalOpen(false)}
                imgData={imgData}
                imgUploadResponse={handleMultipleImageUpload}
                MAX_IMAGES={50}
            />
        </>
    );
};

export default UploadImagePage;
